/**
 * SocialProofStrip — subtle "live activity" chips shown right below
 * the hero on socialpulse.com.
 *
 * Pulls narrative badges from /api/home/social-proof:
 *   • 12 people reading now
 *   • 7 new stories this week
 *   • 3 helpers stepped up
 *
 * Kept intentionally lightweight so it never blocks LCP.
 */
import { useEffect, useState } from "react";
import { httpPublic } from "@/lib/api";
import { Sparkles } from "lucide-react";

const TONE_MAP = {
  blue:    "bg-[#E8F1FF] text-[#1E64C8]",
  orange:  "bg-[#FFF1E4] text-[#B25D38]",
  amber:   "bg-[#FEF3C7] text-[#92400E]",
  emerald: "bg-[#DCFCE7] text-[#065F46]",
  violet:  "bg-[#EDE9FE] text-[#6D28D9]",
  sky:     "bg-[#E0F2FE] text-[#075985]",
};

export default function SocialProofStrip() {
  const [badges, setBadges] = useState([]);

  useEffect(() => {
    let cancelled = false;
    httpPublic.get("/home/social-proof", { params: { limit: 6 } })
      .then((r) => { if (!cancelled) setBadges(r.data?.badges || []); })
      .catch(() => { if (!cancelled) setBadges([]); });
    return () => { cancelled = true; };
  }, []);

  if (badges.length === 0) return null;

  return (
    <section
      className="max-w-[1360px] mx-auto px-6 lg:px-10 pt-2 pb-6"
      data-testid="home-social-proof"
    >
      <div
        className="flex items-center gap-2 overflow-x-auto no-scrollbar"
        role="status"
        aria-live="polite"
      >
        <span className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#50586C] shrink-0">
          <Sparkles className="w-3.5 h-3.5" /> Right now
        </span>
        {badges.map((b, idx) => (
          <span
            key={b.key}
            className={`shrink-0 inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-3 py-1.5 ${TONE_MAP[b.tone] || TONE_MAP.blue} animate-in fade-in slide-in-from-left-1 duration-500`}
            style={{ animationDelay: `${idx * 80}ms` }}
            data-testid={`home-social-proof-${b.key}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
            {b.label}
          </span>
        ))}
      </div>
    </section>
  );
}
